import { classes } from "components/Utility";
import React, { useMemo, useState, useRef, useEffect, useContext } from "react";
import { TooltipContext, TooltipProviderValue } from "./TooltipProvider";

export type ReorderableItem = { id: string; node: React.ReactNode };

export type ReorderableGridProps = {
  items: ReorderableItem[];
  onReorder: (nextIds: string[]) => void;
  className?: string;
  itemClassName?: string;
  handleAriaLabel?: string;
  layout?: "list" | "grid";
  handleSelector?: string;
  clearGhostBg?: boolean; // we add a white background to the ghost by default so it shows over other things when dragging, this will clear it
};

type DragState = {
  id: string;
  offsetX: number;
  offsetY: number;
  width: number;
  height: number;
  left: number;
  right: number;
  x: number;
  y: number;
  containerTop: number;
  containerLeft: number;
};

type ClosureRefs = {
  drag?: DragState;
  order?: string[];
};

const activationDistance = 3; // pixels that you need to move the mouse before the drag starts

const ReorderableGrid = ({
  items,
  onReorder,
  className,
  itemClassName,
  layout = "list",
  handleSelector,
  clearGhostBg = false,
}: ReorderableGridProps) => {
  const tooltip = useContext<TooltipProviderValue>(TooltipContext);
  const propIds = useMemo(() => items.map(i => i.id), [items]);
  const [_order, setOrder] = useState<string[]>(propIds);
  const [_drag, setDrag] = useState<DragState>(null);
  const pointerIdRef = useRef<number>(null);
  const startPointRef = useRef<{ x: number, y: number }>(null);
  const rafRunning = useRef(false);
  const pendingEvent = useRef<PointerEvent>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const closureRefs = useRef<ClosureRefs>({});
  closureRefs.current.drag = _drag;
  closureRefs.current.order = _order;

  // lookup for id to render node
  const idToNode = useMemo(() => {
    const m = new Map<string, React.ReactNode>();
    items.forEach(it => m.set(it.id, it.node));
    return m;
  }, [items]);

  useEffect(() => {
    setOrder(propIds);
  }, [items]);

  // refs for row elements for measurement / list sorting
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const setRowRef = (id: string) => (el: HTMLDivElement) => {
    rowRefs.current.set(id, el);
  };

  const insertAt = (arr: string[], idx: number, id: string) => {
    const next = arr.slice();
    next.splice(Math.max(0, Math.min(idx, next.length)), 0, id);
    return next;
  };

  const computeInsertIndex = (pointerX: number, pointerY: number): number => {
    const ids = closureRefs.current.order;
  
    // pick the element with the closest center
    let best = Infinity;
    let bestIdx = 0;
    
    for (let i = 0; i < ids.length; i++) {
      const el = rowRefs.current.get(ids[i]);
      const elChild = el.firstElementChild;
      
      const r = elChild?.getBoundingClientRect();
      if (!r) continue;

      const cx = (r.left + r.right) / 2;
      const cy = (r.top + r.bottom) / 2;
      const d2 = (pointerX - cx) ** 2 + (pointerY - cy) ** 2;
      
      if (d2 < best) {
        best = d2;
        bestIdx = i;
      }
    }

    return bestIdx;
  };

  // Throttled move loop (RAF)
  const scheduleAnimationFrame = () => {
    if (rafRunning.current) return;
    rafRunning.current = true;
    requestAnimationFrame(() => {
      rafRunning.current = false;
      tooltip?.hide(); // if youre dragging stuff over tooltips you can have a bad time
      const e = pendingEvent.current;
      const drag = closureRefs.current.drag;
      if (!e || !drag || pointerIdRef.current !== e.pointerId) return;

      let x = e.clientX;
      let y = e.clientY;
  
      // clamp within container so the ghost never renders outside
      const rootRect = rootRef.current?.getBoundingClientRect();
      const minY = rootRect.top + drag.offsetY;
      const maxY = rootRect.bottom - (drag.height - drag.offsetY);
      y = Math.max(minY, Math.min(y, maxY));

      const minX = rootRect.left + drag.offsetX;
      const maxX = rootRect.right - (drag.width - drag.offsetX);
      x = Math.max(minX, Math.min(x, maxX));

      // check distance threshold to activate
      if (startPointRef.current !== null && Math.sqrt((x - startPointRef.current.x) ** 2 + (y - startPointRef.current.y) **2) < activationDistance) {
        return;
      } else {
        startPointRef.current = null;
      }

      // Live reorder: remove dragging id and insert at computed index
      const insertIndex = computeInsertIndex(x, y);
      const base = closureRefs.current.order.filter(r => r !== drag.id);
      const next = insertAt(base, insertIndex, drag.id);

      // update ghost position
      setDrag(prev => (prev ? { ...prev, x, y } : prev));
      setOrder(next);
    });
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!closureRefs.current.drag) return;
    if (pointerIdRef.current !== e.pointerId) return;
    pendingEvent.current = e;
    scheduleAnimationFrame();
  };

  // this only works when called from onPointerUp because of how closures work. if you ever want to call this elsewhere, you will have to save the whole function, or its listeners in a ref when you start listening, so you have the right function references to clean up
  // (the refs change every render)
  const cleanupListeners = () => {
    window.removeEventListener("pointermove", onPointerMove as any);
    window.removeEventListener("pointerup", onPointerUp as any);
    window.removeEventListener("pointercancel", onPointerUp as any);
  };

  const onPointerUp = (ev: PointerEvent) => {
    if (!closureRefs.current.drag) return;
    cleanupListeners();
    const finalIds = closureRefs.current.order.slice();
    setDrag(null);
    pointerIdRef.current = null;
    startPointRef.current = null;
    pendingEvent.current = null;
    onReorder(finalIds);
  };

  const maybeStartDrag = (e: React.PointerEvent, id: string) => {
    const target = e.target as Element;
    let captureEl: Element | undefined;

    if (handleSelector) {
      // only start if the press was on/inside a handle element
      captureEl = target?.closest(handleSelector) ?? undefined;
      if (!captureEl) return;
    } else {
      captureEl = e.currentTarget as Element;
    }

    startDrag(e, id, captureEl);
  };

  const startDrag = (e: React.PointerEvent, id: string, captureEl: Element) => {
    e.preventDefault();
    e.stopPropagation();
    
    pointerIdRef.current = e.pointerId;

    const rowEl = rowRefs.current.get(id);
    if (!rowEl) return;
    const r = rowEl.children[0].getBoundingClientRect();

    captureEl.setPointerCapture(e.pointerId);

    startPointRef.current = { x: e.clientX, y: e.clientY };
    const rootRect = rootRef.current?.getBoundingClientRect();

    setDrag({
      id,
      offsetX: e.clientX - r.left,
      offsetY: e.clientY - r.top,
      width: r.width,
      height: r.height,
      left: r.left,
      right: r.right,
      x: e.clientX,
      y: e.clientY,
      containerTop: rootRect?.top ?? 0,
      containerLeft: rootRect?.left ?? 0,
    });

    window.addEventListener("pointermove", onPointerMove as any, { passive: true });
    window.addEventListener("pointerup", onPointerUp as any, { passive: true });
    window.addEventListener("pointercancel", onPointerUp as any, { passive: true });
  };

  const drag = closureRefs.current.drag;

  return (
    <div className={classes("reorderable-grid", className, layout)} ref={rootRef}>
      {closureRefs.current.order.map((id) => {
        const isDragging = drag?.id === id;

        return (
          <div
            key={id}
            ref={setRowRef(id)}
            className={classes("reorderable-item", itemClassName, isDragging && "reorderable-dragging")}
            onPointerDown={(e) => maybeStartDrag(e, id)}
            style={{
              width: isDragging ? drag.width : undefined,
              height: isDragging ? drag.height : undefined,
            }}
          >
            {idToNode.get(id)}
          </div>
        );
      })}

      {drag && (
        <div
          className={classes("reorderable-item reorderable-ghost", !clearGhostBg && "ghost-bg")}
          style={{
            top: Math.round(drag.y - drag.offsetY),
            left: Math.round(layout === "grid" ? (drag.x - drag.offsetX) : drag.left),
            width: Math.round(drag.width),
            height: Math.round(drag.height),
          }}
        >
          {idToNode.get(drag.id)}
        </div>
      )}
    </div>
  );
}

export default ReorderableGrid;
